import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, formatPrice } from "../../lib/api";
import { catLabel } from "../../lib/i18n";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  const load = () => api.get("/products", { params: { include_inactive: true } }).then(({ data }) => setProducts(data));

  /**
   * Müştəriyə göndəriləcək link.
   *
   * Məhsul KODU işlədilir, daxili nömrə yox: məhsul silinib yenidən əlavə
   * olunanda daxili nömrə dəyişir və əvvəl göndərilmiş linklər ölür.
   * Kod isə sahibkarın öz kodudur, dəyişmir.
   */
  const productLink = (p) =>
    `${window.location.origin}/product/${encodeURIComponent(p.code || p.id)}`;

  const copyLink = async (p) => {
    const url = productLink(p);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopyalandı");
    } catch {
      // Bəzi brauzerlərdə icazə olmur — müştəri linki əl ilə köçürsün.
      window.prompt("Linki kopyalayın:", url);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Məhsul silindi");
      load();
    } catch {
      toast.error("Silinmə uğursuz oldu");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6 sm:mb-8">
        <h1 className="font-serif font-light text-3xl sm:text-4xl tracking-tight">Məhsullar</h1>
        <button onClick={() => navigate("/admin/products/new")} data-testid="add-product-btn" className="flex items-center gap-2 bg-brand-ink text-white px-5 py-3 text-sm tracking-wider uppercase rounded-md hover:bg-brand-oxid transition-colors">
          <Plus size={16} /> Yeni məhsul
        </button>
      </div>

      {/* Telefonda kart siyahısı — 7 sütunlu cədvəl ekrana sığmır */}
      <div className="lg:hidden space-y-3">
        {products.map((p) => (
          <div
            key={p.id}
            className="bg-white border border-brand-line rounded-md p-4 flex gap-4"
            data-testid={`admin-product-card-${p.id}`}
          >
            <div className="w-16 h-20 shrink-0 bg-brand-bg2 overflow-hidden rounded">
              <img
                src={imgUrl(p.images && p.images[0])}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = imgUrl(null); }}
              />
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-serif text-base leading-tight">
                {p.name}
                {!p.is_active && <span className="ml-2 text-xs text-destructive">(gizli)</span>}
              </p>
              <p className="text-xs text-brand-muted mt-1">
                <span className="font-mono">{p.code}</span> · {catLabel(p.category, "az")}
              </p>
              <p className="text-sm mt-2">
                {formatPrice(p.price)}
                <span className="text-brand-muted"> · stok </span>
                <span className={p.stock <= 3 ? "text-destructive" : ""}>{p.stock}</span>
              </p>

              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => navigate(`/admin/products/${p.id}`)}
                  data-testid={`edit-product-m-${p.id}`}
                  className="flex-1 flex items-center justify-center gap-2 border border-brand-line rounded py-2.5 text-xs tracking-wider uppercase"
                >
                  <Pencil size={14} strokeWidth={1.5} /> Redaktə
                </button>
                <button
                  onClick={() => copyLink(p)}
                  data-testid={`copy-link-m-${p.id}`}
                  aria-label="Linki kopyala"
                  title="Müştəriyə göndərmək üçün link"
                  className="px-4 border border-brand-line rounded"
                >
                  <Link2 size={15} strokeWidth={1.5} />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid={`delete-product-m-${p.id}`}
                      aria-label="Sil"
                      className="px-4 border border-brand-line rounded text-destructive"
                    >
                      <Trash2 size={15} strokeWidth={1.5} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Məhsulu sil?</AlertDialogTitle>
                      <AlertDialogDescription>{p.name} silinəcək. Bu əməliyyat geri qaytarıla bilməz.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>İmtina</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(p.id)}>Sil</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
        {products.length === 0 && (
          <p className="text-center text-brand-muted py-10">Məhsul yoxdur</p>
        )}
      </div>

      {/* Masaüstü cədvəli */}
      <div className="hidden lg:block bg-white border border-brand-line rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-line text-left">
              <th className="px-5 py-3 overline">Şəkil</th>
              <th className="px-5 py-3 overline">Ad</th>
              <th className="px-5 py-3 overline">Kod</th>
              <th className="px-5 py-3 overline">Bölmə</th>
              <th className="px-5 py-3 overline">Qiymət</th>
              <th className="px-5 py-3 overline">Stok</th>
              <th className="px-5 py-3 overline text-right">Əməliyyat</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-brand-line last:border-0 hover:bg-brand-bg2" data-testid={`admin-product-${p.id}`}>
                <td className="px-5 py-3">
                  <div className="w-12 h-14 bg-brand-bg2 overflow-hidden rounded">
                    <img src={imgUrl(p.images && p.images[0])} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = imgUrl(null); }} />
                  </div>
                </td>
                <td className="px-5 py-3 font-serif text-base">{p.name}{!p.is_active && <span className="ml-2 text-xs text-destructive">(gizli)</span>}</td>
                <td className="px-5 py-3 font-mono text-xs">{p.code}</td>
                <td className="px-5 py-3">{catLabel(p.category, "az")}</td>
                <td className="px-5 py-3">{formatPrice(p.price)}</td>
                <td className="px-5 py-3"><span className={p.stock <= 3 ? "text-destructive" : ""}>{p.stock}</span></td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => copyLink(p)}
                      data-testid={`copy-link-${p.id}`}
                      title="Müştəriyə göndərmək üçün link"
                      className="p-2 hover:bg-brand-line rounded"
                    >
                      <Link2 size={15} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => navigate(`/admin/products/${p.id}`)} data-testid={`edit-product-${p.id}`} className="p-2 hover:bg-brand-line rounded"><Pencil size={15} strokeWidth={1.5} /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-product-${p.id}`} className="p-2 hover:bg-brand-line rounded text-destructive"><Trash2 size={15} strokeWidth={1.5} /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Məhsulu sil?</AlertDialogTitle>
                          <AlertDialogDescription>{p.name} silinəcək. Bu əməliyyat geri qaytarıla bilməz.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>İmtina</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(p.id)} data-testid={`confirm-delete-${p.id}`}>Sil</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-brand-muted">Məhsul yoxdur</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminProducts;
